import CONST from 'const'
import {
  drawGuides,
  getAnchorPoints,
  getGuides,
  getLineGuideStops,
} from 'dataloader/utils/guideLines'
import Konva from 'konva'
import { chunk, cloneDeep, findIndex, round } from 'lodash'
import React, { useState, useRef } from 'react'
import { Group, Line as KonvaLine, Circle } from 'react-konva'
import { jsonParse } from 'utils/json'
import { throttleRaf } from 'utils/throttleRaf'
import { v4 as uuidv4 } from 'uuid'

interface Props {
  selectedMaterials: Array<number | string>
  material: ReduxStore.Materials.Data.IMaterial
  scale: { x: number; y: number }
  onMouseDown: (
    evt: Konva.KonvaEventObject<MouseEvent>,
    material: ReduxStore.Materials.Data.IMaterial
  ) => void
  onDragStart: (overlayId: string | number) => void
  onDragMove: (
    evt: Konva.KonvaEventObject<MouseEvent>,
    material: ReduxStore.Materials.Data.IMaterial
  ) => void
  onDragEnd: (
    evt: Konva.KonvaEventObject<MouseEvent>,
    material: ReduxStore.Materials.Data.IMaterial
  ) => void
  dispatchUpdateCirclePos: (newPos: ReduxStore.Materials.Data.INewCirclePos) => void
  snappingEnabled?: boolean
  snappingDistance?: number
}

interface PointsPos {
  x: number
  y: number
}

const Line: React.FC<Props> = props => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [highlightedCircleIndex, setHighlightedCircleIndex] = useState<number>(-1);
  const lineRef = useRef<Konva.Line>(null);
  
  const {
    selectedMaterials,
    material,
    scale,
    onMouseDown,
    onDragStart,
    onDragMove,
    onDragEnd,
    dispatchUpdateCirclePos,
  } = props
  
  const { overlay_id, shape_data } = material
  const selected = selectedMaterials.some(id => id === overlay_id)
  const color = selected ? CONST.OVERLAY_YELLOW : CONST.OVERLAY_BLUE
  
  const shapeData = jsonParse(shape_data)
  if (!shapeData) {
    return <></>
  }
  
  // Extract stroke width specified in the saved shape data.  The `strokeWidth`
  // value represents the width in *world* units (i.e. before any zoom is
  // applied via the layer transform).  Because the layer itself is scaled, we
  // need to convert this world-space value to the on-screen pixel value that
  // Konva expects.  If we keep the original value when the user zooms out the
  // line will appear disproportionately thick; if the user zooms in it will
  // appear too thin.  Multiplying the world-space width by the current layer
  // scale factor keeps the visual thickness consistent with the rest of the
  // scene.
  const { points, strokeWidth: worldStrokeWidth = 2 } = shapeData

  // Translate the world stroke width to screen/pixel space so that it scales
  // together with the rest of the geometry.
  const strokeWidth = worldStrokeWidth * scale.x
  
  if (!points || points.length < 4 || points.length % 2 !== 0) {
    console.log("Line component is returning empty because points are invalid:", {
      pointsExist: !!points,
      pointsLength: points ? points.length : null,
      isEvenLength: points ? points.length % 2 === 0 : null
    });
    return <></>
  }
  
  // Group points into pairs of [x, y] coordinates
  const chunkedPoints = chunk(points, 2) as Array<[number, number]>
  
  const handleDragEnd = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    evt.cancelBubble = true
    const dx = evt.target.x()
    const dy = evt.target.y()
    
    // Update the position of the line by adding dx, dy to each point
    const newMaterial = cloneDeep(material)
    const newShapeData = jsonParse(newMaterial.shape_data)
    
    if (newShapeData) {
      // Update all points based on the drag delta - works for both normal lines and multilines
      const newPoints = [];
      for (let i = 0; i < points.length; i += 2) {
        newPoints.push(points[i] + dx, points[i + 1] + dy);
      }
      
      newShapeData.points = newPoints;
      newMaterial.shape_data = JSON.stringify(newShapeData)
      
      // Reset position for the group since we've baked the position into the points
      evt.target.x(0)
      evt.target.y(0)
    }
    
    onDragEnd(evt, newMaterial)
  }
  
  const handleMouseEnter = () => {
    setIsHovered(true)
  }
  
  const handleMouseLeave = () => {
    setIsHovered(false)
  }
  
  const eventToCoordinates = (evt: Konva.KonvaEventObject<MouseEvent>): PointsPos => {
    const transform = evt.target.getAbsoluteTransform().copy()
    transform.invert()
    const stage = evt.target.findAncestor('Stage')
    // @ts-ignore
    const pos = transform.point(stage.getPointerPosition())
    return { x: round(pos.x, 2), y: round(pos.y, 2) }
  }
  
  const getObjectSnappingEdges = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    const pos = { x: evt.target.x(), y: evt.target.y() }
    if (pos.x && pos.y) {
      return {
        vertical: [
          {
            guide: Math.round(pos.x),
            offset: 0,
            snap: 'start',
          },
        ],
        horizontal: [
          {
            guide: Math.round(pos.y),
            offset: 0,
            snap: 'start',
          },
        ],
      }
    }
  }
  
  const handleCircleMouseOver = throttleRaf((evt: Konva.KonvaEventObject<MouseEvent>) => {
    const pointIndex = findIndex(
      chunkedPoints,
      point => point[0] === evt.target.x() && point[1] === evt.target.y()
    )
    setHighlightedCircleIndex(pointIndex)
  })
  
  const handleCircleMouseLeave = () => {
    setHighlightedCircleIndex(-1)
  }
  
  const handleCircleDragMove = throttleRaf(
    (evt: Konva.KonvaEventObject<MouseEvent>, pointIndex: number) => {
      evt.cancelBubble = true
      if (!evt.target.getLayer()) {
        return
      }
      evt.target
        .getLayer()
        .find('.guid-line')
        .each((node: Konva.Node) => node.destroy())

      // Apply snapping if enabled
      if (props.snappingEnabled) {
        const lineGuideStops = getLineGuideStops(
          evt.target,
          eventToCoordinates(evt),
          1000 // Use a default snapping distance if not provided
        )
        const itemBounds = getObjectSnappingEdges(evt)
        if (itemBounds) {
          const guides = getGuides(lineGuideStops, itemBounds)
          const { presetX, presetY } = getAnchorPoints(guides)
          
          drawGuides(guides, evt.target)
          if (presetX) {
            evt.target.x(presetX)
          }
          if (presetY) {
            evt.target.y(presetY)
          }
        }
      }

      const posX = round(evt.target.x(), 2)
      const posY = round(evt.target.y(), 2)
      const line = lineRef.current
      if (line) {
        // Update the line's point being dragged
        const points = line.points()
        points[pointIndex * 2] = posX
        points[pointIndex * 2 + 1] = posY
        line.points(points)
      }
    }
  )
  
  const handleCircleDragEnd = (evt: Konva.KonvaEventObject<MouseEvent>, pointIndex: number) => {
    evt.cancelBubble = true
    if (evt.target.getLayer()) {
      evt.target
        .getLayer()
        .find('.guid-line')
        .each((node: Konva.Node) => node.destroy())
    }

    const posX = round(evt.target.x(), 2)
    const posY = round(evt.target.y(), 2)
    dispatchUpdateCirclePos({ pos: [posX, posY], pointIndex, material: cloneDeep(material) })
  }
  
  return (
    <Group
      ref={instance => instance && window.shapeRefs.push(instance)}
      key={`${overlay_id}-${JSON.stringify(points)}-${strokeWidth}`}
      shapeId={overlay_id}
      name={overlay_id.toString()}
      materialBelongsTo={overlay_id}
      shape="line"
      draggable={selected}
      onDragStart={() => onDragStart(overlay_id)}
      onDragMove={evt => onDragMove(evt, material)}
      onDragEnd={handleDragEnd}
    >
      <KonvaLine
        ref={lineRef}
        points={points}
        stroke={color}
        strokeWidth={strokeWidth}
        lineCap="square"
        lineJoin="miter"
        strokeScaleEnabled={false}
        onMouseDown={evt => onMouseDown(evt, material)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        hitStrokeWidth={Math.max(strokeWidth * 3, 10)} // Make it easier to click on the line
      />
      {/* Control points for editing vertices when line is selected */}
      {selected && chunkedPoints.map((point, pointIndex) => (
        <Circle
          draggable={true}
          key={uuidv4()}
          x={point[0]}
          y={point[1]}
          radius={highlightedCircleIndex === pointIndex ? 10 / scale.x : 4 / scale.x}
          stroke="red"
          strokeWidth={Math.round(2 / scale.x)}
          fill={color}
          onMouseDown={evt => evt.evt.stopPropagation()}
          onMouseEnter={handleCircleMouseOver}
          onMouseLeave={handleCircleMouseLeave}
          onDragMove={evt => handleCircleDragMove(evt, pointIndex)}
          onDragEnd={evt => handleCircleDragEnd(evt, pointIndex)}
        />
      ))}
    </Group>
  )
}

export default Line